<template>
  <div class="file-upload">
    <el-upload ref="upload" action="#" :auto-upload="false" :on-change="handleChange" :on-remove="handleRemove"
      :before-upload="beforeUpload" :multiple="multiple" :accept="acceptTypes" :show-file-list="showFileList"
      :disabled="disabled" :limit="limit" :on-exceed="handleExceed" :file-list="fileList">
      <!-- 默认插槽 -->
      <slot>
        <div class="upload-trigger" :class="{ 'is-loading': uploadLoading }" v-if="limit > fileList.length">
          <template v-if="!uploadLoading">
            <i class="el-icon-upload"></i>
            <div class="upload-text">{{ uploadText }}</div>
          </template>
          <template v-else>
            <i class="el-icon-loading"></i>
            <div class="upload-text">上传中...</div>
          </template>
        </div>
      </slot>

      <!-- 提示说明文字 -->
      <template #tip>
        <div class="el-upload__tip" v-if="tip">{{ tip }}</div>
      </template>
    </el-upload>


    <!-- 文件列表预览 -->
    <div class="file-list" v-if="showFileList && fileList.length > 0">
      <!-- 图片列表 -->
      <div v-if="imageFiles.length > 0" class="image-grid" key="image-grid">
        <div v-for="file in imageFiles" :key="file.uid" class="image-item">
          <div class="image-preview" @click="handlePreview(file)">
            <el-image :src="file.url" :preview-src-list="[file.url]" class="preview-image">
            </el-image>
          </div>
          <div class="preview-actions">
            <i class="el-icon-close" @click="handleDelete(file)"></i>
          </div>
        </div>
      </div>
      <div v-for="(file, index) in nonImageFiles" :key="index" class="file-item">

        <!-- 视频预览 -->
        <video v-if="isVideoFile(file)" :src="file.url" class="preview-video" controls v-show="file.url">
        </video>
        <!-- 文档预览 -->
        <div v-if="isDocFile(file)" class="doc-preview">
          <i class="el-icon-document"></i>
          <span class="file-name">{{ file.name }}</span>
        </div>
        <!-- 上传进度 -->
        <div class="upload-progress" v-if="file.status === 'uploading'">
          <el-progress type="circle" :percentage="file.percentage || 0" :width="36" :stroke-width="4"></el-progress>
        </div>
        <!-- 文件状态 -->
        <!-- <div class="file-status" :class="file.status">
          <i class="el-icon-loading" v-if="file.status === 'uploading'"></i>
          <i class="el-icon-success" v-if="file.status === 'success'"></i>
          <i class="el-icon-error" v-if="file.status === 'error'"></i>
        </div> -->
        <!-- 预览操作按钮 -->
        <div class="preview-actions">
          <i class="el-icon-delete" @click="handleDelete(file)"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadImage } from '@/api/new_api'

export default {
  name: 'FileUpload',
  props: {
    value: {
      type: [String, Array],
      default: () => []
    },
    // 允许的文件类型数组 [2, 3] 表示允许视频和文档
    allowedTypes: {
      type: Array,
      default: () => [2, 3]
    },
    // 是否允许多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 是否显示文件列表
    showFileList: {
      type: Boolean,
      default: true
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 文件数量限制
    limit: {
      type: Number,
      default: 10
    },
    // 文件大小限制(MB)
    maxSize: {
      type: Object,
      default: () => ({
        video: 500,
        document: 50
      })
    },
    // 上传提示文字
    uploadText: {
      type: String,
      default: '点击上传视频或文档'
    },
    // 提示说明文字
    tip: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      uploadLoading: false,
      fileList: [],
      videoTypes: ['video/mp4', 'video/webm', 'video/ogg'],
      docTypes: ['doc', 'docx', 'pdf', 'ppt', 'pptx'],
      showViewer: false,
      previewUrl: '',
      imageTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp']
    }
  },

  computed: {
    // 接受的文件类型
    acceptTypes() {
      const types = []
      if (this.allowedTypes.includes(1)) {
        types.push('image/*')
      }
      if (this.allowedTypes.includes(2)) {
        types.push('video/*')
      }
      if (this.allowedTypes.includes(3)) {
        types.push('.doc,.docx,.pdf,.ppt,.pptx')
      }
      return types.join(',')
    },
    // 分离图片文件和非图片文件
    imageFiles() {
      return this.fileList.filter(file => this.isImageFile(file))
    },

    nonImageFiles() {
      return this.fileList.filter(file => !this.isImageFile(file))
    },

    // 文件大小限制（字节）
    maxSizeBytes() {
      return {
        video: this.maxSize.video * 1024 * 1024,
        document: this.maxSize.document * 1024 * 1024
      }
    }
  },

  watch: {
    value: {
      handler(val) {
        if (Array.isArray(val)) {
          this.fileList = val.map(url => ({
            name: this.getFileNameFromUrl(url),
            url: url
          }))
        } else if (val) {
          this.fileList = [{
            name: this.getFileNameFromUrl(val),
            url: val
          }]
        } else {
          this.fileList = []
        }
      },
      immediate: true
    }
  },

  methods: {
    // 获取文件名从URL
    getFileNameFromUrl(url) {
      return url.split('/').pop()
    },
    // 判断是否是图片
    isImageFile(file) {
      if (!file) return false

      if (file.raw) {
        return file.raw.type?.startsWith('image/') || false
      }

      if (!file.name) return false

      const extension = file.name.toLowerCase().split('.').pop()
      return this.imageTypes.includes(extension)
    },

    // 判断是否为视频文件
    isVideoFile(file) {
      return file.raw ?
        file.raw.type.startsWith('video/') :
        this.videoTypes.some(type => file.name.toLowerCase().endsWith(type.split('/')[1]))
    },

    // 判断是否为文档文件
    isDocFile(file) {
      const extension = file.name.toLowerCase().split('.').pop()
      return this.docTypes.includes(extension)
    },

    // 获取文件类型
    getFileType(file) {
      if (this.isVideoFile(file)) return 2
      if (this.isDocFile(file)) return 3
      if (this.isImageFile(file)) return 1
      return null
    },

    // 上传前验证
    beforeUpload(file) {
      const fileType = this.getFileType(file)

      // 验证文件类型
      if (!fileType) {
        this.$message.error('不支持的文件类型')
        return false
      }

      if (!this.allowedTypes.includes(fileType)) {
        this.$message.error('当前不允许上传该类型的文件')
        return false
      }

      // 验证文件大小
      const maxSize = fileType === 2 ? this.maxSizeBytes.video : this.maxSizeBytes.document
      if (file.size > maxSize) {
        const sizeLimit = fileType === 2 ? this.maxSize.video : this.maxSize.document
        this.$message.error(`文件大小不能超过 ${sizeLimit}MB`)
        return false
      }

      return true
    },

    // 文件状态改变
    async handleChange(file, fileList) {
      try {
        file.status = 'uploading'
        file.percentage = 0
        this.uploadLoading = true

        const fileType = this.getFileType(file)
        const res = await uploadImage({
          file: file.raw,
          type: fileType
        }, progressEvent => {
          // 处理上传进度回调
          if (progressEvent.lengthComputable) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            file.percentage = percentage
          }
        })

        if (res.code === 1) {
          // 上传成功
          file.status = 'success'
          file.percentage = 100
          // 更新文件列表中的URL
          const index = fileList.findIndex(f => f.uid === file.uid)
          if (index !== -1) {
            fileList[index].url = res.data.uri
            fileList[index].status = 'success'
          }

          this.fileList = fileList

          // 触发事件
          const urls = this.fileList.map(f => f.url).filter(Boolean)
          this.$emit('input', this.multiple ? urls : urls[0])
          this.$emit('success', {
            name: file.name,
            url: res.data.uri,
            type: fileType,
            response: res.data
          })

          this.$message.success('上传成功')
        } else {
          throw new Error(res.msg || '上传失败')
        }
      } catch (error) {
        file.status = 'error'
        this.$message.error(error.message || '上传失败')
        // 移除失败文件
        const index = fileList.findIndex(f => f.uid === file.uid)
        if (index !== -1) {
          fileList.splice(index, 1)
        }
        this.fileList = fileList
      } finally {
        this.uploadLoading = false
      }
    },

    // 文件超出数量限制
    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传 ${this.limit} 个文件`)
    },

    // 移除文件
    handleRemove(file) {
      const index = this.fileList.findIndex(f => f.uid === file.uid)
      if (index !== -1) {
        this.fileList.splice(index, 1)
        const urls = this.fileList.map(f => f.url).filter(Boolean)
        this.$emit('input', this.multiple ? urls : urls[0])
        this.$emit('remove', file)
      }
    },

    // 删除文件
    handleDelete(file) {
      this.$confirm('确定删除该文件？', '提示', {
        type: 'warning'
      }).then(() => {
        this.handleRemove(file)
      }).catch(() => { })
    },

    // 手动触发上传
    submit() {
      this.$refs.upload.submit()
    },

    // 清空上传列表
    clearFiles() {
      this.$refs.upload.clearFiles()
      this.fileList = []
      this.$emit('input', this.multiple ? [] : '')
    },
    // 获取文档图标
    getDocIcon(file) {
      const extension = file.name.toLowerCase().split('.').pop()
      const iconMap = {
        pdf: 'el-icon-document',
        doc: 'el-icon-document-word',
        docx: 'el-icon-document-word',
        ppt: 'el-icon-document-ppt',
        pptx: 'el-icon-document-ppt'
      }
      return iconMap[extension] || 'el-icon-document'
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024
        i++
      }
      return `${bytes.toFixed(1)} ${units[i]}`
    },

    // 处理图片预览
    handlePreview(file) {
      if (this.isImageFile(file) && file.url) {
        this.previewUrl = file.url
        this.showViewer = true
      }
    },
    // 关闭预览
    closeViewer() {
      this.showViewer = false
      this.previewUrl = ''
    },

    // 处理下载
    handleDownload(file) {
      if (!file.url) return
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  }
}
</script>

<style lang="scss" scoped>
.file-upload {
  .upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    margin-top: 10px;
    cursor: pointer;
    transition: border-color 0.3s;

    &:hover {
      border-color: #409EFF;
    }

    &.is-loading {
      cursor: not-allowed;
      opacity: 0.7;
    }

    .el-icon-upload {
      font-size: 28px;
      color: #8c939d;
      margin-bottom: 8px;
    }

    .upload-text {
      font-size: 14px;
      color: #606266;
    }
  }

  .file-list {
    margin-top: 10px;

    .image-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-bottom: 12px;

      .image-item {
        position: relative;
        aspect-ratio: 1;
        border-radius: 4px;
        overflow: hidden;
        border: 1px solid #e4e7ed;

        .image-preview {
          width: 100%;
          height: 100%;
          cursor: pointer;

          :deep .el-image {
            width: 100%;
            height: 100%;

            img {
              width: 100%;
              height: 100%;
            }
          }

          .preview-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s;
          }

          .image-hover-mask {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(0, 0, 0, 0.3);
            opacity: 0;
            transition: opacity 0.3s;

            i {
              font-size: 24px;
              color: #fff;
            }
          }

          &:hover {
            .preview-image {
              transform: scale(1.05);
            }

            .image-hover-mask {
              opacity: 1;
            }
          }
        }

        .preview-actions {
          position: absolute;
          top: 0px;
          right: 0px;

          opacity: 0;
          transition: opacity 0.3s;
          background-color: rgba(107, 103, 103, 0.9);
          padding: 4px;
          width: 15px;
          height: 15px;
          border: 1px solid rgba(107, 103, 103, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;

          i {
            font-size: 13px;
            color: #fff;
            cursor: pointer;

            &:hover {
              color: #F56C6C;
            }
          }
        }

        &:hover .preview-actions {
          opacity: 1;
        }
      }
    }

    .file-item {
      position: relative;
      margin-bottom: 10px;
      padding: 8px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;

      &:hover .preview-actions {
        opacity: 1;
      }

      .preview-video {
        width: 100%;
        max-height: 200px;
        object-fit: contain;
      }

      .doc-preview {
        display: flex;
        align-items: center;
        padding: 8px;

        .el-icon-document {
          font-size: 24px;
          color: #909399;
          margin-right: 8px;
        }

        .file-name {
          color: #606266;
          font-size: 14px;
        }
      }

      .preview-actions {
        position: absolute;
        top: 8px;
        right: 8px;
        opacity: 0;
        transition: opacity 0.3s;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 4px;
        border-radius: 4px;

        i {
          font-size: 16px;
          color: #909399;
          cursor: pointer;
          margin: 0 4px;

          &:hover {
            color: #409EFF;
          }

          &.el-icon-delete:hover {
            color: #F56C6C;
          }
        }
      }

      .image-preview {
        position: relative;
        width: 100%;
        height: 150px;
        cursor: pointer;
        overflow: hidden;
        border-radius: 4px;

        .preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .image-hover-mask {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(0, 0, 0, 0.3);
          opacity: 0;
          transition: opacity 0.3s;

          i {
            font-size: 24px;
            color: #fff;
          }
        }

        &:hover {
          .preview-image {
            transform: scale(1.05);
          }

          .image-hover-mask {
            opacity: 1;
          }
        }
      }

      /* 文件大小样式 */
      .file-size {
        position: absolute;
        bottom: 8px;
        left: 8px;
        font-size: 12px;
        color: #909399;
      }

      /* 上传进度样式 */
      .upload-progress {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 2;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
        padding: 10px;
      }

      /* 文件状态样式 */
      .file-status {
        position: absolute;
        top: 8px;
        right: 40px;

        &.success i {
          color: #67C23A;
        }

        &.error i {
          color: #F56C6C;
        }
      }
    }
  }
}

/* 列表动画 */
.file-list-enter-active,
.file-list-leave-active {
  transition: all 0.5s ease;
}

.file-list-enter-from,
.file-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.file-list-move {
  transition: transform 0.5s ease;
}
</style>